import React, { useState, useEffect } from 'react';
import { messagingService } from '../../../services/messagingService';
import { 
  X, 
  User, 
  Crown, 
  Shield, 
  Phone, 
  Mail, 
  MoreVertical,
  UserPlus,
  Search,
  Circle
} from 'lucide-react';

const TeamRoster = ({ group, canModerate, onClose }) => {
  const [participants, setParticipants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);

  useEffect(() => {
    if (group?.participants) {
      setParticipants(group?.participants);
    }
  }, [group]);

  useEffect(() => {
    if (showAddMembers && canModerate) {
      loadAllUsers();
    }
  }, [showAddMembers, canModerate]);

  const loadAllUsers = async () => {
    try {
      setLoading(true);
      const users = await messagingService?.getAllUsers();
      // Filter out users who are already participants
      const existingUserIds = participants?.map(p => p?.userId);
      const availableUsers = users?.filter(u => !existingUserIds?.includes(u?.id));
      setAllUsers(availableUsers);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'city_official': case'city official':
        return <Shield className="w-4 h-4 text-green-600" />;
      case 'pcf_admin': case'pcf admin':
        return <Crown className="w-4 h-4 text-purple-600" />;
      default:
        return <User className="w-4 h-4 text-blue-600" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'city_official': case'city official':
        return 'bg-green-100 text-green-800';
      case 'pcf_admin': case'pcf admin':
        return 'bg-purple-100 text-purple-800';
      case 'student':
        return 'bg-blue-100 text-blue-800';
      case 'business':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOnlineStatus = (userId) => {
    // Placeholder for online status - would integrate with real presence system
    return Math.random() > 0.3; // Simulate online status
  };

  // Filter participants based on search
  const filteredParticipants = participants?.filter(participant =>
    participant?.user?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    participant?.user?.role?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  // Filter available users for adding
  const filteredAvailableUsers = allUsers?.filter(user =>
    user?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    user?.role?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Team Roster</h3>
          <p className="text-sm text-gray-500">{participants?.length} members</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {/* Search and Actions */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        
        {canModerate && (
          <button
            onClick={() => setShowAddMembers(!showAddMembers)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {showAddMembers ? 'Cancel' : 'Add Members'}
          </button>
        )}
      </div>
      {/* Members List */}
      <div className="flex-1 overflow-y-auto">
        {showAddMembers && canModerate ? (
          // Add Members View
          (<div className="p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Available Users</h4>
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)]?.map((_, i) => (
                  <div key={i} className="flex items-center space-x-3 animate-pulse">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredAvailableUsers?.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                {allUsers?.length === 0 ? 'All users are already members' : 'No users found'}
              </p>
            ) : (
              <div className="space-y-2">
                {filteredAvailableUsers?.map((user) => (
                  <div
                    key={user?.id}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {user?.fullName || 'Unknown'}
                        </p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user?.role)}`}>
                          {user?.role || 'User'}
                        </span>
                      </div>
                    </div>
                    <button
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                      onClick={() => {
                        // Add member logic would go here
                        console.log('Add member:', user?.id);
                      }}
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>)
        ) : (
          // Current Members View
          (<div className="p-4">
            {filteredParticipants?.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No members found</p>
            ) : (
              <div className="space-y-3">
                {filteredParticipants?.map((participant) => {
                  const isOnline = getOnlineStatus(participant?.userId);
                  
                  return (
                    <div key={participant?.userId} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-md">
                      {/* Avatar with Online Status */}
                      <div className="relative">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          {getRoleIcon(participant?.user?.role)}
                        </div>
                        <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
                          isOnline ? 'bg-green-400' : 'bg-gray-400'
                        }`}></div>
                      </div>
                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {participant?.user?.fullName || 'Unknown User'}
                          </p>
                          {participant?.isAdmin && (
                            <Crown className="w-3 h-3 text-yellow-500" />
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(participant?.user?.role)}`}>
                            {participant?.user?.role || 'User'}
                          </span>
                          <div className="flex items-center space-x-1">
                            {participant?.user?.phone && (
                              <Phone className="w-3 h-3 text-gray-400" />
                            )}
                            <Circle className={`w-2 h-2 ${isOnline ? 'text-green-500' : 'text-gray-400'} fill-current`} />
                            <span className="text-xs text-gray-500">
                              {isOnline ? 'Online' : 'Offline'}
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center space-x-1">
                        <button
                          className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                          title="Send direct message"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        {canModerate && !participant?.isAdmin && (
                          <button className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>)
        )}
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{participants?.filter(p => getOnlineStatus(p?.userId))?.length} online</span>
          <span>{participants?.length} total members</span>
        </div>
      </div>
    </div>
  );
};

export default TeamRoster;